import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleLeft,
  faArrowLeft,
  faClose,
} from "@fortawesome/free-solid-svg-icons";
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { NotificationManager } from "react-notifications";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { AuthApi } from "states/api";
import { useStores } from "_common/hooks";
import Loading from "_common/component/Loading";
import classNames from "classnames";
import i18next from "i18next";
import InputPassword from "_common/component/InputPassword";
import { useNotify } from "_common/component/NotifyProvider";

function RegisterPage() {
  const navigate = useNavigate();
  const {
    authStore: { updateToken, updateUser, languages, language, general },
  } = useStores();
  const [params] = useSearchParams();
  const [activeTab, setActiveTab] = useState("email");
  const { pushNotify } = useNotify();

  const schema = yup
    .object({
      username: yup.string().required(i18next.t("verify_require_username")),
      password: yup.string().required(i18next.t("verify_require_password")),
    })
    .required();

  const ref_no = params?.get("ref_no");
  const type = params?.get("type") || "email";

  const { mutate, isLoading } = useMutation(
    (variables) => AuthApi.register(variables),
    {
      onSuccess: (res) => {
        pushNotify({
          type: "register",
        });
        updateToken(res?.data);
        updateUser(res?.data);
        setTimeout(() => {
          navigate("/board");
        }, 200);
      },
      onError: (error) => {
        const errorMessage =
          i18next.t(error?.message) ?? i18next.t("action_failed_msg");
        NotificationManager.error(errorMessage);
      },
    }
  );

  const {
    register,
    reset,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const handleRegister = (values) => {
    if (isLoading) return;
    mutate(values);
  };

  useEffect(() => {
    if (!ref_no) return;
    setValue("ref_no", ref_no);
  }, [ref_no]);

  const langData = useMemo(
    () => languages?.find((obj) => obj?.code === language),
    [languages, language]
  );

  return (
    <>
      <div className="sticky top-0 left-0 w-full px-6 py-6  flex items-center justify-between gap-2 bg-[#000000]">
        <div className="cursor-pointer">
          <FontAwesomeIcon
            icon={faArrowLeft}
            style={{ fontSize: "22px", color: "#fff" }}
            onClick={() => navigate(-1)}
          />
        </div>
        <Link to="/auth/login">
          <span className="text-[#3D5AFE]">{i18next.t("login")}</span>
        </Link>
      </div>
      <form className="auth-sign-in" onSubmit={handleSubmit(handleRegister)}>
        {isLoading && <Loading />}
        <div className="sbui-space-col sbui-space-y-6 pr-[1rem] pl-[1rem]">
          <h2 className="pb-[0.5rem]">{i18next.t("note30")}</h2>
          <div className="tabs !mt-0">
            <div
              className={classNames("tab", {
                active: activeTab === "email",
              })}
              onClick={() => setActiveTab("email")}
            >
              E-mail
            </div>
            <div
              className={classNames("tab", {
                active: activeTab === "phone",
              })}
              onClick={() => setActiveTab("phone")}
            >
              {i18next.t("phone")}
            </div>
          </div>
          <input
            type="text"
            className="sbui-input"
            placeholder={i18next.t("username")}
            {...register("username")}
          />
          {activeTab === "phone" && (
            <input
              type="text"
              className="sbui-input"
              placeholder={i18next.t("phone")}
              {...register("phone")}
            />
          )}
          {activeTab === "email" && (
            <input
              type="text"
              className="sbui-input"
              placeholder={i18next.t("E-mail")}
              {...register("email")}
            />
          )}
          <InputPassword
            autoComplete="current-password"
            type="password"
            className="sbui-input"
            placeholder={i18next.t("set_password")}
            {...register("password")}
          />
          {Number(general?.allow_ref_no) === 1 && (
            <input
              disabled={!!ref_no}
              type="text"
              className="sbui-input"
              placeholder={i18next.t("ref_no")}
              {...register("ref_no")}
            />
          )}
          <div className="sbui-space-col sbui-space-y-6">
            <span className="sbui-btn-container sbui-btn--w-full">
              <button
                className="text-[#ffffff] font-[600] !bg-[#1678ff] w-full min-h-[44px]"
                type="submit"
                disabled={!isValid || isLoading}
              >
                <span>{i18next.t("register")}</span>
              </button>
            </span>
          </div>
          <div className="flex  flex-col items-center justify-center mt-4">
            <Link className="z-20" to="/">
              <img
                className="w-auto h-[52px]"
                src={general?.logo_dark}
                alt="codex"
              />
            </Link>
            <Link
              className="flex justify-center items-center gap-2 cursor-pointer p-1 mt-3 rounded hover:bg-[#19181d]"
              to="/language"
            >
              <img className="w-[20px]" src={langData?.image} alt="" />
              <span className="text-[14px] text-white">{langData?.text}</span>
            </Link>
          </div>
        </div>
      </form>
    </>
  );
}

export default RegisterPage;
